import React, { useMemo, useState } from 'react'
import StarRatingHalf from './rating'
import type { StarRatingHalfValue } from './rating'
import { useSubmitPaymentFeedbackMutation } from 'store/subscription'
import { L10N } from 'global/localization'
import { toast } from 'react-toastify'

export type FeedbackOption = 1 | 2 | 3 | 4 | 5 | 6 | null
export type FeedbackPayload = {
  rating: StarRatingHalfValue
  option: FeedbackOption
  otherText?: string
}

type Props = {
  totalForm: number[]
  invoice?: string | null
}

type StatusMap = Record<number, { submitted: boolean }>

export default function FeedbackPopup({ totalForm, invoice }: Props) {
  const [submitPaymentFeedback] = useSubmitPaymentFeedbackMutation()

  const [rating, setRating] = useState<StarRatingHalfValue>(0)
  const [option, setOption] = useState<FeedbackOption>(1)
  const [otherText, setOtherText] = useState('')
  const [loading, setLoading] = useState(false)

  // useState with array number (length controls how many popups)
  const [formIds] = useState<number[]>(totalForm)
  // submit status object
  const [status, setStatus] = useState<StatusMap>({})

  const disableSubmit = useMemo(() => {
    if (rating <= 0) return true
    if (option === 6 && otherText.trim().length === 0) return true
    return false
  }, [rating, option, otherText])

  // show only ONE popup at a time: first not-submitted index
  const currentIndex = useMemo(() => {
    for (let i = 0; i < formIds.length; i++) {
      if (!status[i]?.submitted) return i
    }
    return -1 // all submitted
  }, [formIds.length, status])

  const done = currentIndex === -1

  const onClose = () => {
    setStatus((prev) => ({
      ...prev,
      [currentIndex]: { submitted: true }
    }))
  }

  const handleSubmit = () => {
    console.log(`Submitted. Rating: ${rating}. Other Text: ${otherText}. Option: ${option}`)

    submitPaymentFeedback({
      feedbackId: formIds[currentIndex],
      optionValue: option,
      rating: rating,
      other: otherText,
      invoiceNo: invoice
    })
      .unwrap()
      .then((res) => {
        toast.info(
          L10N.getLanguage() === 'en-US'
            ? 'Thank you! Your feedback has been successfully submitted.'
            : 'Umpan balik Anda telah dikirim.'
        ) // TODO:Should get from data master.

        clearSubmission()

        setStatus((prev) => ({
          ...prev,
          [currentIndex]: { submitted: true }
        }))
      })
      .catch(console.error)
  }

  const clearSubmission = () => {
    setOption(1)
    setOtherText('')
    setRating(0)
  }

  return (
    <div>
      {!done && (
        <>
          {/* overlay */}
          <div
            className='fixed inset-0 z-40 bg-[#0B1B3A]/50'
            onClick={onClose}
            aria-hidden='true'
          />

          {/* wrapper */}
          <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
            {/* card */}
            <div className='w-full max-w-[420px] rounded-2xl bg-white shadow-2xl'>
              {/* header */}
              <div className='relative px-5 pt-5 pb-3 border-b border-gray-100'>
                <h2 className='text-center text-[15px] font-semibold text-gray-900'>
                  {L10N.LABEL.TITLE_PAYMENT_FEEDBACK}
                </h2>

                <button
                  type='button'
                  onClick={onClose}
                  aria-label='Close'
                  className='absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100'
                >
                  ✕
                </button>
              </div>

              {/* content */}
              <div className='px-5 py-4'>
                {/* Q1 */}
                <p className='text-[13px] font-semibold text-gray-800 leading-5'>
                  {L10N.LABEL.MAIN_QUESTION_FEEDBACK}
                </p>

                <div className='mt-2 flex items-center gap-2'>
                  <StarRatingHalf
                    value={rating}
                    onChange={setRating}
                    sizeClassName='w-7 h-7'
                    ariaLabel='Rating kemudahan'
                  />
                </div>

                {/* legend like screenshot */}
                <div className='mt-2 text-[11px] text-gray-600 leading-5'>
                  <div className='flex flex-wrap gap-x-4'>
                    <span>1 = {L10N.LABEL.RATING_FEEDBACK_1}</span>
                    <span>2 = {L10N.LABEL.RATING_FEEDBACK_2}</span>
                    <span>4 = {L10N.LABEL.RATING_FEEDBACK_4}</span>
                  </div>
                  <div className='flex flex-wrap gap-x-4'>
                    <span>3 = {L10N.LABEL.RATING_FEEDBACK_3}</span>
                    <span>5 = {L10N.LABEL.RATING_FEEDBACK_5}</span>
                  </div>
                </div>

                {/* divider */}
                <div className='my-4 h-px bg-gray-100' />

                {/* Q2 */}
                <p className='text-[13px] font-semibold text-gray-800 leading-5'>
                  {L10N.LABEL.QUESTION_PAYMENT_FEEDBACK}
                </p>

                <div className='mt-3 space-y-2'>
                  <RadioRow
                    checked={option === 1}
                    onChange={() => {
                      setOption(1)
                      setOtherText('')
                    }}
                    label={L10N.LABEL.ANSWER_FEEDBACK_1}
                  />
                  <RadioRow
                    checked={option === 2}
                    onChange={() => {
                      setOption(2)
                      setOtherText('')
                    }}
                    label={L10N.LABEL.ANSWER_FEEDBACK_2}
                  />
                  <RadioRow
                    checked={option === 3}
                    onChange={() => {
                      setOption(3)
                      setOtherText('')
                    }}
                    label={L10N.LABEL.ANSWER_FEEDBACK_3}
                  />
                  <RadioRow
                    checked={option === 4}
                    onChange={() => {
                      setOption(4)
                      setOtherText('')
                    }}
                    label={L10N.LABEL.ANSWER_FEEDBACK_4}
                  />
                  <RadioRow
                    checked={option === 5}
                    onChange={() => {
                      setOption(5)
                      setOtherText('')
                    }}
                    label={L10N.LABEL.ANSWER_FEEDBACK_5}
                  />

                  {/* Lainnya + input, mirip screenshot */}
                  <div className='flex items-start gap-3'>
                    <input
                      type='radio'
                      className='mt-1 h-4 w-4 accent-blue-600'
                      checked={option === 6}
                      onChange={() => setOption(6)}
                    />
                    <div className='flex-1'>
                      <div className='text-[12px] text-gray-800'>
                        {L10N.LABEL.ANSWER_FEEDBACK_6}:
                      </div>
                      <input
                        maxLength={250}
                        value={otherText}
                        onChange={(e) => {
                          setOtherText(e.target.value)
                        }}
                        onFocus={() => setOption(6)}
                        placeholder={
                          L10N.getLanguage() === 'en-US'
                            ? 'Write your reason...'
                            : 'Tulis alasan Anda...'
                        }
                        className='mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-[12px] outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100'
                      />
                    </div>
                  </div>
                </div>

                <div className='mt-4 text-center text-[11px] text-gray-500'>
                  {L10N.LABEL.INFO_FEEDBACK}
                </div>
              </div>

              {/* footer actions */}
              <div className='px-5 pb-5'>
                <button
                  type='button'
                  onClick={handleSubmit}
                  disabled={disableSubmit || loading}
                  className='w-full rounded-lg bg-blue-600 py-2.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {/* {loading ? 'Mengirim...' : 'Kirim'} */}
                  {L10N.LABEL.BUTTON_LABEL_FEEDBACK}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function RadioRow({
  checked,
  onChange,
  label
}: {
  checked: boolean
  onChange: () => void
  label: string
}) {
  return (
    <label className='flex items-start gap-3 cursor-pointer select-none'>
      <input
        type='radio'
        className='mt-1 h-4 w-4 accent-blue-600'
        checked={checked}
        onChange={onChange}
      />
      <span className='text-[12px] text-gray-800 leading-5'>{label}</span>
    </label>
  )
}
